/**
 * NarrationDB — IndexedDB wrapper for offline chapter audio caching.
 *
 * Stage 1 of the offline narration plan:
 *   - Save per-chapter MP3 Blobs into IndexedDB after streaming from the backend.
 *   - Load cached Blobs for offline/repeat playback without a network round-trip.
 *
 * Database: "narration-offline-db"  (version 1)
 * Object store: "chapters"
 *   key: `{project_id}:{chapter_id}`
 *   value: { key, project_id, chapter_id, blob, byte_length, saved_at }
 */

const DB_NAME    = 'narration-offline-db';
const STORE_NAME = 'chapters';
const DB_VERSION = 1;

function _openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror   = (e) => reject(new Error(`NarrationDB open failed: ${e.target.error}`));
  });
}

function _key(projectId, chapterId) {
  return `${projectId}:${chapterId}`;
}

/**
 * Stream a chapter audio file from the backend and cache it in IndexedDB.
 * Falls back silently if IndexedDB is unavailable (e.g. private browsing).
 *
 * @param {string} projectId
 * @param {string} chapterId
 * @param {string} apiBase — e.g. "https://…/api"
 * @param {Function} [onProgress] — called with { loaded, total } as bytes arrive
 * @returns {Promise<string>} Object URL suitable for <audio src>
 */
export async function streamAndCacheChapter(projectId, chapterId, apiBase, onProgress) {
  const url = `${apiBase}/projects/${projectId}/narration/chapters/${chapterId}/audio-stream`;

  // Try to load from cache first
  try {
    const cached = await loadChapterAudio(projectId, chapterId);
    if (cached) {
      return URL.createObjectURL(cached);
    }
  } catch {
    // cache miss or IDB unavailable — continue to stream
  }

  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error(`Stream fetch failed: ${res.status}`);

  const contentLength = parseInt(res.headers.get('Content-Length') || '0', 10);
  const reader        = res.body.getReader();
  const chunks        = [];
  let   loaded        = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.byteLength;
    onProgress?.({ loaded, total: contentLength || loaded });
  }

  const blob = new Blob(chunks, { type: 'audio/mpeg' });

  // Persist to IndexedDB (best-effort)
  try {
    await saveChapterAudio(projectId, chapterId, blob);
  } catch (e) {
    console.warn('[NarrationDB] cache save failed (non-fatal):', e);
  }

  return URL.createObjectURL(blob);
}

/**
 * Save a chapter audio Blob directly to IndexedDB.
 * @param {string} projectId
 * @param {string} chapterId
 * @param {Blob}   blob
 */
export async function saveChapterAudio(projectId, chapterId, blob) {
  const db = await _openDB();
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req   = store.put({
      key:         _key(projectId, chapterId),
      project_id:  projectId,
      chapter_id:  chapterId,
      blob,
      byte_length: blob.size,
      saved_at:    new Date().toISOString(),
    });
    req.onsuccess = () => resolve(true);
    req.onerror   = (e) => reject(e.target.error);
  });
}

/**
 * Load a cached chapter Blob from IndexedDB.
 * Returns null if not cached or IDB unavailable.
 * @param {string} projectId
 * @param {string} chapterId
 * @returns {Promise<Blob|null>}
 */
export async function loadChapterAudio(projectId, chapterId) {
  const db = await _openDB();
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req   = store.get(_key(projectId, chapterId));
    req.onsuccess = (e) => resolve(e.target.result?.blob ?? null);
    req.onerror   = (e) => reject(e.target.error);
  });
}

/**
 * Return chapter IDs that are cached for a given project.
 * @param {string} projectId
 * @returns {Promise<string[]>}
 */
export async function getCachedChapterIds(projectId) {
  const db = await _openDB();
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req   = store.getAll();
    req.onsuccess = (e) => {
      const ids = (e.target.result || [])
        .filter((r) => r.project_id === projectId)
        .map((r) => r.chapter_id);
      resolve(ids);
    };
    req.onerror = (e) => reject(e.target.error);
  });
}

/**
 * Delete a specific cached chapter from IndexedDB.
 */
export async function deleteChapterAudio(projectId, chapterId) {
  const db = await _openDB();
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req   = store.delete(_key(projectId, chapterId));
    req.onsuccess = () => resolve(true);
    req.onerror   = (e) => reject(e.target.error);
  });
}

/**
 * Return full metadata records for all chapters cached for a project.
 * Each record: { key, project_id, chapter_id, byte_length, saved_at } (no blob).
 * @param {string} projectId
 * @returns {Promise<Array>}
 */
export async function getAllCachedChapters(projectId) {
  const db = await _openDB();
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req   = store.getAll();
    req.onsuccess = (e) => {
      const rows = (e.target.result || [])
        .filter((r) => r.project_id === projectId)
        .map(({ blob: _b, ...meta }) => meta);   // strip blob from metadata
      resolve(rows);
    };
    req.onerror = (e) => reject(e.target.error);
  });
}

/**
 * Storage stats for a project: total cached chapters + bytes.
 * @param {string} projectId
 * @returns {Promise<{count: number, totalBytes: number}>}
 */
export async function getStorageStats(projectId) {
  const db = await _openDB();
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req   = store.getAll();
    req.onsuccess = (e) => {
      const rows = (e.target.result || []).filter((r) => r.project_id === projectId);
      resolve({
        count:      rows.length,
        totalBytes: rows.reduce((s, r) => s + (r.byte_length || 0), 0),
      });
    };
    req.onerror = (e) => reject(e.target.error);
  });
}

/**
 * Delete every cached chapter for a project.
 * @param {string} projectId
 * @returns {Promise<number>} count of deleted records
 */
export async function clearAllProjectChapters(projectId) {
  const db   = await _openDB();
  const ids  = await getCachedChapterIds(projectId);
  return new Promise((resolve, reject) => {
    const tx    = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    let done = 0;
    if (ids.length === 0) { resolve(0); return; }
    ids.forEach((cid) => {
      const r = store.delete(_key(projectId, cid));
      r.onsuccess = () => { done++; if (done === ids.length) resolve(done); };
      r.onerror   = (e) => reject(e.target.error);
    });
  });
}
